"use client";

import Translations from "@translate/components/EmailLogin/steps/CodeVerifyStepMobile";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import type { CodeVerifyStepProps } from "../types";
import { SIGN_IN_PAGE_COPY } from "../signInPageCopy";
import { formatEmailForDisplay } from "../shared/formatEmailForDisplay";
import { CodeInputGroup, focusFirstCodeInput } from "../shared/CodeInputGroup";
import { ResendCodeRow } from "../shared/ResendCodeRow";
import { VerifyCodeButton } from "../shared/VerifyCodeButton";
import { useKeyboardInsets } from "@/src/hooks";
import { useOnboardingHarnessQuery } from "@/src/hooks/onboarding/useOnboardingHarnessQuery";

const I18n = I18nUtil.createScoped(Translations);
const CODE_LENGTH = 6;

export function CodeVerifyStepMobile({
  email,
  code,
  onCodeChange,
  onVerify,
  verifying = false,
  codeError = false,
  resendCountdown,
  onResend,
  className,
  useV301VerifyChrome = false,
  onInputFocusChange,
  autoSubmitEnabled = true,
}: CodeVerifyStepProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const prevCodeErrorRef = useRef<boolean>(codeError);
  const formRef = useRef<HTMLDivElement>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const keyboardInsets = useKeyboardInsets();
  const useWebKeyboardInset = keyboardInsets.shouldApplyWebInsetPadding;
  const harness = useOnboardingHarnessQuery();
  const displayEmail = useMemo(() => formatEmailForDisplay(email), [email]);
  const titleEmail = useMemo(() => email.trim().toLowerCase(), [email]);
  const fullCode = code.join("");
  const isCodeComplete = fullCode.length === CODE_LENGTH;
  const canVerify = isCodeComplete && !verifying;

  useEffect(() => {
    if (harness.enabled) {
      return;
    }
    focusFirstCodeInput(inputRefs, "raf");
  }, [harness.enabled]);

  useEffect(() => {
    const root = formRef.current;
    if (!root) {
      return;
    }
    const onFocusIn = () => {
      setIsInputFocused(true);
      onInputFocusChange?.(true);
    };
    const onFocusOut = () => {
      setIsInputFocused(false);
      onInputFocusChange?.(false);
    };
    root.addEventListener("focusin", onFocusIn);
    root.addEventListener("focusout", onFocusOut);
    return () => {
      root.removeEventListener("focusin", onFocusIn);
      root.removeEventListener("focusout", onFocusOut);
    };
  }, [onInputFocusChange]);

  useEffect(() => {
    const prev = prevCodeErrorRef.current;
    prevCodeErrorRef.current = codeError;
    if (!codeError || prev) {
      return;
    }
    focusFirstCodeInput(inputRefs, "raf");
  }, [codeError]);

  useEffect(() => {
    if (!isCodeComplete || verifying || autoSubmitEnabled === false) {
      return;
    }
    onVerify();
  }, [isCodeComplete, verifying, autoSubmitEnabled, onVerify]);

  const title = useV301VerifyChrome
    ? SIGN_IN_PAGE_COPY.verifyTitle
    : I18n.t("Check your email");
  const subtitlePrefix = useV301VerifyChrome
    ? SIGN_IN_PAGE_COPY.verifySubtitlePrefix
    : I18n.t("We sent a 6-digit code to");
  const errorMessage = useV301VerifyChrome
    ? SIGN_IN_PAGE_COPY.invalidCode
    : I18n.t("Invalid verification code");

  const footerClassName = cn(
    "shrink-0 transition-all duration-200",
    useV301VerifyChrome
      ? cn("pt-14", !isInputFocused && "mb-safe")
      : cn("mb-6 pt-4", useWebKeyboardInset && isInputFocused && "pb-keyboard"),
  );

  const rootClassName = cn(
    "flex w-full flex-col",
    useV301VerifyChrome ? undefined : "min-h-0 h-full",
    useV301VerifyChrome &&
      useWebKeyboardInset &&
      isInputFocused &&
      "pb-keyboard",
    className,
  );

  const formClassName = cn(
    "flex flex-col",
    useV301VerifyChrome ? "gap-8" : "min-h-0 flex-1 gap-0 overflow-y-auto",
  );

  return (
    <div className={rootClassName} data-testid="code-verify-step-mobile">
      <div
        ref={formRef}
        data-testid={
          useV301VerifyChrome ? "verify-email-form" : "verify-email-form-scroll"
        }
        className={formClassName}
      >
        <div
          className={cn(
            "flex shrink-0 flex-col",
            useV301VerifyChrome ? "gap-3" : "gap-2",
          )}
          data-testid="verify-email-title-block"
        >
          <h2
            aria-label="Code Verify Step h2"
            data-openreplay-label="Code Verify Step h2 Semantic"
            id="sem-code-verify-step-h2"
            data-testid="verify-email-title"
            className={cn(
              "text-content_primary",
              useV301VerifyChrome ? "headline_regular_m" : "headline_regular_l",
            )}
          >
            {title}
          </h2>
          <div
            className="flex min-w-0 flex-wrap items-start gap-1"
            title={titleEmail}
            data-testid="verify-email-subtitle-row"
          >
            <p className="body_medium_m text-content_secondary">
              {subtitlePrefix}
            </p>
            <p className="body_medium_m text-content_primary">{displayEmail}</p>
          </div>
        </div>

        <div
          className={cn(
            "flex shrink-0 flex-col gap-4",
            useV301VerifyChrome ? "" : "mt-8",
          )}
          data-testid="verify-email-form-section"
        >
          <div
            className="flex flex-col gap-2"
            data-testid="verify-email-code-error-group"
          >
            <CodeInputGroup
              variant="mobile"
              code={code}
              onCodeChange={onCodeChange}
              codeError={codeError}
              inputRefs={inputRefs}
              useV301Chrome={useV301VerifyChrome}
            />
            {codeError && (
              <p
                className="body_s text-content_accent2_primary"
                role="alert"
                data-testid="verify-email-error-message"
              >
                {errorMessage}
              </p>
            )}
          </div>
          <ResendCodeRow
            resendCountdown={resendCountdown}
            onResend={onResend}
            codeError={codeError}
          />
        </div>
      </div>

      <div className={footerClassName} data-testid="verify-email-cta-footer">
        <VerifyCodeButton
          variant="mobile"
          canVerify={canVerify}
          verifying={verifying}
          onVerify={onVerify}
          forceEnabledAppearance={useV301VerifyChrome}
        />
      </div>
    </div>
  );
}
